import { useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { AttachmentThumbnail } from './AttachmentThumbnail';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';
import { isPreviewable } from '../utils/fileTypeUtils';
import type { TicketAttachmentResponse } from '../types/tickets';

interface AttachmentItemProps {
  attachment: TicketAttachmentResponse;
  variant?: 'compact' | 'full';
  onDownload: (attachmentId: string, fileName: string) => void;
  siblings?: TicketAttachmentResponse[];
  // Shows a delete button (admins on the staff ticket page); the caller confirms and deletes
  onDelete?: (attachment: TicketAttachmentResponse) => void;
}

export function AttachmentItem({ attachment, variant = 'full', onDownload, siblings, onDelete }: AttachmentItemProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isCompact = variant === 'compact';
  const canPreview = isPreviewable(attachment.contentType);

  // Filter siblings to only previewable attachments for cycling
  const previewableSiblings = siblings?.filter((a) => isPreviewable(a.contentType));
  const initialIndex = previewableSiblings?.findIndex((a) => a.id === attachment.id) ?? 0;

  const handleClick = () => {
    if (canPreview) {
      setPreviewOpen(true);
    } else {
      onDownload(attachment.id, attachment.fileName);
    }
  };

  const item = (
    <button
      onClick={handleClick}
      className={`group w-full flex items-center gap-${isCompact ? '2' : '3'} p-${isCompact ? '1.5' : '2'} rounded${isCompact ? '' : '-md'} hover:bg-muted/50 transition-colors text-left`}
    >
      <AttachmentThumbnail
        attachment={attachment}
        size={isCompact ? 'sm' : 'md'}
      />
      <div className="flex-1 min-w-0">
        <div className={`text-${isCompact ? 'xs' : 'sm'} font-medium text-foreground truncate`}>
          {attachment.fileName}
        </div>
        <div className="text-xs text-muted-foreground">
          {attachment.fileSizeFormatted}
          {!isCompact && attachment.uploadedBy && ` · ${attachment.uploadedBy}`}
          {!isCompact && attachment.uploadedAt && ` · ${new Date(attachment.uploadedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
        </div>
      </div>
      <Download
        className={`h-${isCompact ? '3' : '4'} w-${isCompact ? '3' : '4'} text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`}
      />
    </button>
  );

  return (
    <>
      {onDelete ? (
        <div className="flex items-center gap-1">
          <div className="flex-1 min-w-0">{item}</div>
          <button
            type="button"
            onClick={() => onDelete(attachment)}
            className="p-1.5 rounded text-muted-foreground hover:text-red-600 hover:bg-muted/50 transition-colors flex-shrink-0"
            aria-label={`Delete ${attachment.fileName}`}
            title="Delete attachment"
          >
            <Trash2 className={isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
          </button>
        </div>
      ) : (
        item
      )}

      {canPreview && (
        <AttachmentPreviewModal
          attachment={attachment}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          onDownload={onDownload}
          siblings={previewableSiblings}
          initialIndex={initialIndex >= 0 ? initialIndex : 0}
        />
      )}
    </>
  );
}
